import { isRichTextEmpty } from './richText';
import { JOB_LISTING_JOB_TYPE_OPTIONS, JOB_LISTING_JOB_MODE_OPTIONS } from '../constants/jobListingFormOptions';

/** Field limits aligned with server/validators/jobValidator.js `validateJob`. */
export const JOB_LISTING_FIELD_LIMITS = {
  title: { min: 2, max: 255 },
  company: { min: 2, max: 150 },
  location: { max: 255 },
  salary: { max: 100 },
  experience: { max: 100 },
  description: { max: 10000 },
  source: { max: 100 },
  customIndustry: { min: 1, max: 50 },
};

/**
 * Sanitize salary input: preserve string text/numbers/ranges, strip newlines/tabs, and enforce max length.
 */
export function sanitizeSalaryInput(value, maxLength = JOB_LISTING_FIELD_LIMITS.salary.max) {
  return String(value ?? '')
    .replace(/[\r\n\t]+/g, ' ')
    .slice(0, maxLength);
}

export const JOB_LISTING_FIELD_ORDER = [
  'title',
  'company',
  'location',
  'description',
  'salary',
  'jobType',
  'jobMode',
  'experience',
  'source',
  'lastApplicationDate',
  'industryIds',
  'skills',
];

const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

const formatDateOnlyLocal = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/** Local calendar date for `<input type="date" min>` (today inclusive). */
export const getTodayDateInput = () => formatDateOnlyLocal(new Date());

const isValidIsoDate = (value) => {
  if (!ISO_DATE_RE.test(value)) return false;
  const [year, month, day] = value.split('-').map(Number);
  const parsed = new Date(Date.UTC(year, month - 1, day));
  return (
    parsed.getUTCFullYear() === year &&
    parsed.getUTCMonth() === month - 1 &&
    parsed.getUTCDate() === day
  );
};

export const jobListingFieldValidators = {
  title: (value) => {
    const trimmed = String(value ?? '').trim();
    if (!trimmed) return 'Job title is required';
    const { min, max } = JOB_LISTING_FIELD_LIMITS.title;
    if (trimmed.length < min || trimmed.length > max) {
      return `Job title must be between ${min} and ${max} characters`;
    }
    return '';
  },

  company: (value) => {
    const trimmed = String(value ?? '').trim();
    if (!trimmed) return 'Company name is required';
    const { min, max } = JOB_LISTING_FIELD_LIMITS.company;
    if (trimmed.length < min || trimmed.length > max) {
      return `Company name must be between ${min} and ${max} characters`;
    }
    return '';
  },

  location: (value) => {
    const trimmed = String(value ?? '').trim();
    if (!trimmed) {
      return 'Please search and select a location from the list';
    }
    const { max } = JOB_LISTING_FIELD_LIMITS.location;
    if (trimmed.length > max) {
      return `Location must be less than ${max} characters`;
    }
    return '';
  },

  description: (value) => {
    if (isRichTextEmpty(value)) return '';
    const { max } = JOB_LISTING_FIELD_LIMITS.description;
    if (String(value).length > max) {
      return `Description must be less than ${max} characters`;
    }
    return '';
  },

  salary: (value) => {
    const trimmed = String(value ?? '').trim();
    if (!trimmed) return '';
    const { max } = JOB_LISTING_FIELD_LIMITS.salary;
    if (trimmed.length > max) {
      return `Salary range must be less than ${max} characters`;
    }
    return '';
  },

  jobType: (value) => {
    const trimmed = String(value ?? '').trim();
    if (!trimmed) return '';
    if (!JOB_LISTING_JOB_TYPE_OPTIONS.includes(trimmed)) {
      return 'Job type must be Full-time, Part-time, Contract, Internship, or Temporary';
    }
    return '';
  },

  jobMode: (value) => {
    const trimmed = String(value ?? '').trim();
    if (!trimmed) return '';
    if (!JOB_LISTING_JOB_MODE_OPTIONS.includes(trimmed)) {
      return 'Job mode must be Remote, On-site, or Hybrid';
    }
    return '';
  },

  experience: (value) => {
    const trimmed = String(value ?? '').trim();
    if (!trimmed) return '';
    const { max } = JOB_LISTING_FIELD_LIMITS.experience;
    if (trimmed.length > max) {
      return `Experience required must be less than ${max} characters`;
    }
    return '';
  },

  source: (value) => {
    const trimmed = String(value ?? '').trim();
    if (!trimmed) return '';
    const { max } = JOB_LISTING_FIELD_LIMITS.source;
    if (trimmed.length > max) {
      return `Source must be less than ${max} characters`;
    }
    return '';
  },

  lastApplicationDate: (value) => {
    const trimmed = String(value ?? '').trim();
    if (!trimmed) return 'Last application date is required';
    if (!isValidIsoDate(trimmed)) {
      return 'Last application date must be a valid date';
    }
    if (trimmed < getTodayDateInput()) {
      return 'Last application date must be today or a future date';
    }
    return '';
  },

  skills: (value) => {
    if (!Array.isArray(value) || value.length === 0) {
      return 'At least one skill is required';
    }
    if (!value.every((skill) => typeof skill === 'string' && skill.trim().length > 0)) {
      return 'Skills must be an array of non-empty strings';
    }
    return '';
  },

  industryIds: (_value, formData = {}) => {
    const ids = Array.isArray(formData.industryIds) ? formData.industryIds : [];
    if (ids.length === 0) {
      return 'At least one industry is required';
    }
    return '';
  },
};

/**
 * Validate the job listing form against backend create/update rules.
 * @returns {{ isValid: boolean, fieldErrors: Record<string, string>, firstField: string|null, message: string }}
 */
export function validateJobListingForm(formData = {}) {
  const fieldErrors = {};

  for (const field of JOB_LISTING_FIELD_ORDER) {
    const validator = jobListingFieldValidators[field];
    if (!validator) continue;
    const message = validator(formData[field], formData);
    if (message) fieldErrors[field] = message;
  }

  const firstField =
    JOB_LISTING_FIELD_ORDER.find((field) => fieldErrors[field]) || null;

  return {
    isValid: Object.keys(fieldErrors).length === 0,
    fieldErrors,
    firstField,
    message: firstField
      ? 'Please correct the highlighted fields and try again.'
      : '',
  };
}

/** Map API validation paths (e.g. salaryRange) onto form field names. */
export function mapJobListingApiField(field) {
  const key = String(field || '');
  if (key === 'salaryRange') return 'salary';
  if (key === 'experienceRequired') return 'experience';
  if (key === 'customIndustries' || key.startsWith('customIndustries.')) {
    return 'industryIds';
  }
  if (key === 'industryIds' || key.startsWith('industryIds.') || key === '') {
    if (key === '') return null;
    return 'industryIds';
  }
  return key;
}

export function mapJobListingApiErrorsToFields(errors) {
  if (!Array.isArray(errors)) return {};

  return errors.reduce((acc, item) => {
    if (!item || typeof item !== 'object') return acc;
    const mapped = mapJobListingApiField(item.path || item.param || item.field);
    const message = item.msg || item.message;
    if (mapped && message && !acc[mapped]) {
      acc[mapped] = message;
    }
    return acc;
  }, {});
}
